<template>
  <div class="d-flex align-center">
    <strong class="mr-2">Is Distributor:</strong>
    <v-switch
      :model-value="modelValue"
      color="success"
      hide-details
      density="compact"
      :loading="loading"
      :disabled="disabled"
      @update:model-value="onToggle"
    ></v-switch>
  </div>
</template>

<script>
/**
 * DistributorToggle - Reusable component for toggling client distributor status.
 *
 * Usage:
 *   <DistributorToggle
 *     :client-id="clientId"
 *     :model-value="item.is_distributor"
 *     :token="token"
 *     :disabled="!canEdit"
 *     @updated="onDistributorUpdated"
 *   />
 *
 * Props:
 *   - clientId (Number, required): The client ID to toggle distributor status for
 *   - modelValue (Boolean): Current is_distributor value
 *   - token (String, required): Auth token for API call
 *   - disabled (Boolean): Whether the toggle is disabled (default: false)
 *
 * Events:
 *   - updated(newValue): Emitted after successful API update with the new boolean value
 *   - error(message): Emitted if API call fails
 */
import { defineComponent } from 'vue';
import { updateClientDistributorStatus } from "@/composables/useGetClientsAPI";
import { log } from "@/composables/useLog";

export default defineComponent({
  name: 'DistributorToggle',
  props: {
    clientId: { type: Number, required: true },
    modelValue: { type: Boolean, default: false },
    token: { type: String, required: true },
    disabled: { type: Boolean, default: false }
  },
  emits: ['updated', 'error'],
  data() {
    return {
      loading: false
    }
  },
  methods: {
    async onToggle(newValue) {
      this.loading = true;
      try {
        const response = await updateClientDistributorStatus(this.token, this.clientId, newValue);
        if (response.error) {
          this.$emit('error', response.message || 'Error updating distributor status');
        } else {
          this.$emit('updated', newValue);
        }
      } catch (err) {
        log("Error toggling distributor status:", err);
        this.$emit('error', 'Error updating distributor status');
      } finally {
        this.loading = false;
      }
    }
  }
});
</script>
